import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    MenuItem,
    Box,
    Typography,
    Divider,
    Button,
    Slide,
    InputBase,
    TextField,
    Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { addServiceRequest, getServiceRequest, updateServiceRequest } from '../../../Services/authService';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import SuccessNotification from '../../../Components/Common/SuccessNotification';
import { StyledTextField } from '../../../Components/Common/StyledTextField';
import LoadingAnimation from '../../../Components/Common/LoadingAnimation';
import LocationPopup from '../LocationPopup';
import { DropDownIcon } from '../../../Components/Layouts/CustomIcons';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from "@mui/icons-material/Cancel";
import countryCodes from '../../../Components/Layouts/full_country_calling_codes.json';

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function ServiceRequestModal({ open, onClose, selectedCard, fetchServiceRequests }) {
    const { t } = useTranslation();
    const { mode } = useThemeContext();

    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const fileColor = mode === 'dark' ? DARKCOLORS.Secondary700 : LIGHTCOLORS.Secondary700;
    const modalBackgrounColor = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const errorTextColor = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
    const dropCircle = mode === 'dark' ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
    const dropIcon = mode === 'dark' ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
    const settingIconColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const borderColor2 = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;

    const [formData, setFormData] = useState({
        machineNumber: '',
        issueCategory: '',
        issueType: '',
        description: '',
        firstName: '',
        lastName: '',
        position: '',
        contactNumber: '',
        contactMail: ''
    });

    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [fileError, setFileError] = useState(''); // holds i18n key so it re-translates on language switch
    const [successNotification, setSuccessNotification] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false); const [files, setFiles] = useState([]); // For new uploads
    const [existingFiles, setExistingFiles] = useState([]); // For already uploaded images    
    const [deletedMediaIds, setDeletedMediaIds] = useState([]);
    const [locationError, setLocationError] = useState(false);
    const [machineLocation, setMachineLocation] = useState(null);
    const [countryCode, setCountryCode] = useState({ name: "Germany", dial_code: "+49" });
    const [contactNumber, setContactNumber] = useState('');

    // Reset every field back to its initial state (used on close and after save)
    const resetForm = () => {
        setFormData({
            machineNumber: '',
            issueCategory: '',
            issueType: '',
            description: '',
            firstName: '',
            lastName: '',
            position: '',
            contactNumber: '',
            contactMail: ''
        });
        setErrors({});
        setGeneralError('');
        setFileError('');
        setLocationError(false);
        setMachineLocation(null);
        setFiles([]);
        setExistingFiles([]);
        setDeletedMediaIds([]);
        setCountryCode({ name: "Germany", dial_code: "+49" });
        setContactNumber('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        // Only (re)populate while the modal is open; keying on `open` too means
        // reopening the same card refills the form after a close-time reset.
        if (!open) return;
        setFiles([]);
        setMachineLocation(null)
        setExistingFiles([]);
        setDeletedMediaIds([])
        const fetchServiceRequest = async () => {
            try {
                setGeneralError('');
                setErrors({});
                if (selectedCard) {
                    setFormData(selectedCard); // Pre-fill the form with the selected card data for editing
                    setFormData({
                        machineNumber: selectedCard?.machineNumber,
                        issueCategory: selectedCard?.issueCategory,
                        issueType: selectedCard?.issueType,
                        description: selectedCard?.description,
                        firstName: selectedCard?.firstName,
                        lastName: selectedCard?.lastName,
                        position: selectedCard?.position,
                        contactNumber: selectedCard?.contactNumber,
                        contactMail: selectedCard?.contactMail
                    });
                    const fullPhone = selectedCard?.contactNumber || '';
                    const matchedCode = countryCodes.find(code => fullPhone.startsWith(code.dial_code));

                    if (matchedCode) {
                        setCountryCode(matchedCode);
                        setContactNumber(fullPhone.replace(matchedCode.dial_code, '').trim());
                    } else {
                        setContactNumber(fullPhone);
                    }
                    setMachineLocation(selectedCard?.location)
                    const data = await getServiceRequest(selectedCard.id);
                    if (data?.media) {
                        setExistingFiles(data.media); // Store existing images
                    } else {
                        setExistingFiles([]);
                    }
                } else {
                    setFormData({
                        machineNumber: '',
                        issueCategory: '',
                        issueType: '',
                        description: '',
                        firstName: '',
                        lastName: '',
                        position: '',
                        contactNumber: '',
                        contactMail: ''
                    });
                }
            } catch (error) {
                console.error("Error fetching service request:", error);
            }
        };

        fetchServiceRequest();
    }, [selectedCard, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Allow only numbers and "+" at the beginning
        if (name === "contactNumber") {
            if (!/^\+?[0-9]*$/.test(value)) return;
        }

        // Names are capped at 100 characters while typing
        if ((name === "firstName" || name === "lastName") && value.length > 100) return;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.machineNumber) {
            setLocationError(true); // Highlight "Select Location" in red
        } else {
            setLocationError(false); // Remove red highlight if location is selected
        }
        if (!formData.issueCategory) newErrors.issueCategory = 'Issue Category is required.';
        if (!formData.issueType) newErrors.issueType = 'Issue Type is required.';
        if (!formData.description) newErrors.description = 'Description is required.';
        if (!formData.firstName) newErrors.firstName = 'First Name is required.';
        else if (formData.firstName.trim().length < 2 || formData.firstName.trim().length > 100) newErrors.firstName = t('membershipCards.nameMustBe2to100');
        if (!formData.lastName) newErrors.lastName = 'Last Name is required.';
        else if (formData.lastName.trim().length < 2 || formData.lastName.trim().length > 100) newErrors.lastName = t('membershipCards.nameMustBe2to100');
        if (!contactNumber) newErrors.contactNumber = t('serviceRequest.ContactNumber');
        else if (!/^\d{5,15}$/.test(contactNumber)) newErrors.contactNumber = t('membershipCards.phoneNumberMustBe5to15');

        if (!formData.contactMail) {
            newErrors.contactMail = 'Contact Mail is required.';
        } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.contactMail)) {
            newErrors.contactMail = 'Invalid email format.';
        }
        setErrors(newErrors);
        setGeneralError(
            Object.keys(newErrors)?.length > 0
                ? (newErrors.contactNumber || newErrors.firstName || newErrors.lastName || t('membershipCards.Pleasefill'))
                : ''
        );
        return Object.keys(newErrors)?.length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        console.log('goingg');

        try {
            setLoading(true)
            formData.contactNumber = `${countryCode.dial_code} ${contactNumber}`.trim();

            const formDataToSend = new FormData();
            formDataToSend.append("request", JSON.stringify(formData));
            // Append files
            if (files) {
                files.forEach((file) => {
                    formDataToSend.append("attachments", file);
                });
            }
            // Append deleted media IDs
            if (deletedMediaIds.length > 0) {
                // Append deletedMediaIds correctly
                deletedMediaIds.forEach(id => {
                    formDataToSend.append("deletedMediaIds", id);
                });
            }
            if (selectedCard?.id) {
                await updateServiceRequest(selectedCard?.id, formDataToSend); // Update the card
            } else {
                await addServiceRequest(formDataToSend); // Create a new card
            }

            // Reset the form
            resetForm();
            setSuccessNotification(true); // Show success notification
            onClose(); // Close the dialog
            fetchServiceRequests(); // Refresh the table data
        } catch (error) {
            setGeneralError(error?.response?.data?.detail);
            console.error('Error creating request:', error);
        } finally {
            setLoading(false)
        }
    };

    const handleLocationSubmit = (selectedMachineDetails) => {
        setLocationError(false)
        setFormData((prevFormData) => ({
            ...prevFormData,
            machineNumber: selectedMachineDetails?.machineNo || "", // Ensure it doesn't break if null
        }));
        setMachineLocation(selectedMachineDetails?.location || "")
    };

    const handleLocationClick = () => setIsPopupOpen(true);
    const handleLocationPopupClose = () => setIsPopupOpen(false);

    const MAX_PHOTOS = 5;
    const MAX_VIDEOS = 5;

    const handleFileUpload = (event) => {
        const uploadedFiles = Array.from(event.target.files);
        event.target.value = ''; // allow re-selecting the same file after a rejection
        const isVideoFile = (f) => f.type.startsWith("video");
        const currentVideos = files.filter(isVideoFile).length + existingFiles.filter((f) => isVideo(f.url)).length;
        const currentPhotos = files.length + existingFiles.length - currentVideos;
        const newVideos = uploadedFiles.filter(isVideoFile).length;
        const newPhotos = uploadedFiles.length - newVideos;
        if (currentVideos + newVideos > MAX_VIDEOS || currentPhotos + newPhotos > MAX_PHOTOS) {
            setFileError('serviceRequest.maxFilesLimit');
            return;
        }
        setFileError('');
        setFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFileError('');
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleRemoveExistingFile = (index) => {
        setFileError('');
        const removedFile = existingFiles[index];

        if (removedFile && removedFile.id) {
            setDeletedMediaIds((prev) => {
                if (!prev.includes(removedFile.id)) {
                    const updatedDeletedMediaIds = [...prev, removedFile.id];
                    return updatedDeletedMediaIds;
                }
                return prev;
            });
        }

        setExistingFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const isVideo = (url) => {
        return url.match(/\.(mp4|mov|avi|wmv|flv|webm|mkv|mpeg|mpg|3gp|ogv)$/i);
    };

    return (
        <>
            {loading && <LoadingAnimation />}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        position: 'absolute',
                        right: 10,
                        margin: 0,
                        height: '100vh', // Full height
                        borderRadius: 3,
                        background: modalBackgrounColor,
                        width: "97%",
                        "@media (max-width: 427px)": {
                            right: 5,
                        },
                    },
                }}
            >
                {/* Title with Close Icon */}
                <DialogTitle sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "20px 24px ",

                    }
                }}>
                    <span className='franchise-heading-text'>{t('serviceRequest.serviceRequest')}</span>
                    <IconButton onClick={handleClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{
                    overflowY: 'auto', paddingTop: 2,
                    p: {
                        xs: 2,  // base padding
                        sm: 3,  // small screens
                        md: "20px 24px ",
                    }
                }}>
                    <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Location Selector acting as a button */}
                        <Button id="machineStreet1"
                            onClick={handleLocationClick}
                            sx={{
                                borderRadius: "5px",
                                textAlign: "center",
                                padding: "16px 13px",
                                display: "flex",
                                alignItems: "center",
                                color: locationError ? errorTextColor : settingIconColor,
                                background: inputBoxColor,
                                textTransform: 'none',
                                justifyContent: "space-between",
                                gap: "8px",
                                minWidth: "160px",
                                fontSize: "15px !important",
                            }}
                            endIcon={<DropDownIcon color1={dropCircle} color2={dropIcon} />}
                        >
                            {machineLocation ? `Selected Location: ${machineLocation}` : `${t("transactions.selectLocation")} *`}
                        </Button>
                        {/* Location Popup */}
                        <LocationPopup
                            open={isPopupOpen}
                            onClose={handleLocationPopupClose}
                            selectedMachine={selectedCard?.machineNumber}
                            onSubmit={handleLocationSubmit}
                        />

                        {/* Dropdowns for issue category */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {/* <StyledTextField
                                label={t('serviceRequest.issueCategory')}
                                name="issueCategory"
                                value={formData.issueCategory}
                                onChange={handleChange}
                                error={errors.issueCategory}
                                inputBoxColor={inputBoxColor}
                                required
                            >
                                <MenuItem value="Hardware">{t('franchise.Hardware')}</MenuItem>
                            </StyledTextField> */}
                            <StyledTextField
                                label={t('serviceRequest.issueCategory')}
                                name="issueCategory"
                                type="text"
                                value={formData.issueCategory}
                                onChange={handleChange}
                                error={errors.issueCategory}
                                inputBoxColor={inputBoxColor}
                                required
                            />
                        </Box>

                        {/* Dropdowns for issue type */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {/* <StyledTextField
                                label={t('serviceRequest.issueType')}
                                name="issueType"
                                value={formData.issueType}
                                onChange={handleChange}
                                error={errors.issueType}
                                inputBoxColor={inputBoxColor}
                                required
                            >
                                <MenuItem value="Hardware">{t('franchise.Hardware')}</MenuItem>
                            </StyledTextField> */}
                            <StyledTextField
                                label={t('serviceRequest.issueType')}
                                name="issueType"
                                value={formData.issueType}
                                onChange={handleChange}
                                error={errors.issueType}
                                inputBoxColor={inputBoxColor}
                                required
                            />
                        </Box>

                        <Box >
                            <Box
                            sx={{ background: inputBoxColor, borderRadius: "4px" }}
                            >
                            <StyledTextField
                                label={t('serviceRequest.descriptionPlaceholder')}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                inputBoxColor='transparent'
                                error={errors.description}
                                required
                                multiline={true}
                                minRows={4}
                            />


                            </Box>
                            <Divider sx={{ my: 2,  }} />
                            <Box sx={{ p: 1, background: inputBoxColor, borderRadius: "4px" }}>
                                <label htmlFor="file-input" style={{ cursor: "pointer", display: "flex", alignItems: "center", marginBottom: existingFiles.length > 0 ? "10px" : 0 }}>
                                    <Box sx={{
                                        display: 'flex', justifyContent: "center", alignItems: "center",
                                        background: modalBackgrounColor, padding: "8px", borderRadius: "50%", mr: 1
                                    }}>
                                        <AttachFileIcon sx={{ color: fileColor, fontSize: "20px", rotate: "190deg" }} />
                                    </Box>
                                    <Typography style={{ color: settingIconColor , }}> {t('serviceRequest.UploadPhotosvideos')}</Typography>
                                </label>
                                <input
                                    id="file-input"
                                    type="file"
                                    accept="image/*, video/*"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload}
                                />
                                {fileError && (
                                    <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
                                        {t(fileError)}
                                    </Typography>
                                )}
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: files.length > 1 ? 2 : 0 }}>
                                    {files.map((file, index) => (
                                        <Box key={index} sx={{ position: "relative", width: 80, height: 80 }}>
                                            {file.type.startsWith("video") ? (
                                                <video src={URL.createObjectURL(file)} width={80} height={80} controls style={{ objectFit: "cover" }} />
                                            ) : (
                                                <img src={URL.createObjectURL(file)} alt="uploaded" width={80} height={80} style={{ objectFit: "cover" }} />
                                            )}
                                            <IconButton
                                                size="small"
                                                sx={{ position: "absolute", top: -8, right: -8, background: modalBackgrounColor }}
                                                onClick={() => handleRemoveFile(index)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    {/* Existing files from API response */}
                                    {existingFiles.length > 0 && existingFiles.map((file, index) => (
                                        <Box key={index} sx={{ position: "relative", width: 80, height: 80 }}>
                                            {isVideo(file.url) ? (
                                                <video src={file.url} width={80} height={80} controls style={{ objectFit: "cover" }} />
                                            ) : (
                                                <img src={file.url} alt="existing-file" width={80} height={80} style={{ objectFit: "cover" }} />
                                            )}
                                            <IconButton
                                                size="small"
                                                sx={{ position: "absolute", top: -8, right: -8, background: modalBackgrounColor }}
                                                onClick={() => handleRemoveExistingFile(index)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                        {/* Divider */}
                        <Divider/>

                        {/* Contact Details */}
                        <Typography variant="subtitle1" className='add-machine'>
                            {t('serviceRequest.ContactDetails')}
                        </Typography>
                        <StyledTextField
                            label={t('loginPage.firstname')}
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            error={errors.firstName}
                            inputBoxColor={inputBoxColor}
                            required
                        />
                        <StyledTextField
                            label={t('loginPage.lastname')}
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            error={errors.lastName}
                            inputBoxColor={inputBoxColor}
                            required
                        />
                        <StyledTextField
                            label={`${t('inventory.position')}`}
                            name="position"
                            value={formData.position}
                            onChange={handleChange}
                            error={errors.position}
                            inputBoxColor={inputBoxColor}
                        />
                        <StyledTextField
                            label={t('loginPage.email')}
                            name="contactMail"
                            value={formData.contactMail}
                            onChange={handleChange}
                            error={errors.contactMail}
                            inputBoxColor={inputBoxColor}
                            required
                        />
                        {/* <StyledTextField
                            label={t('loginPage.phoneNumber')}
                            name="contactNumber"
                            value={formData.contactNumber}
                            onChange={handleChange}
                            error={errors.contactNumber}
                            inputBoxColor={inputBoxColor}
                            required
                        />
                        {errors.contactNumber && (
                            <Typography sx={{ color: errorTextColor, fontSize: '12px', marginTop: '-10px' }}>
                                {errors.contactNumber}
                            </Typography>
                        )} */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    backgroundColor: inputBoxColor,
                                    height: '50px',
                               
                                }}
                            >
                                <Autocomplete
                                    options={countryCodes}
                                    getOptionLabel={(option) => `${option.name} (${option.dial_code})`}
                                    value={countryCode}
                                    onChange={(e, newValue) => {
                                        if (newValue) setCountryCode(newValue);
                                    }}
                                    isOptionEqualToValue={(option, value) => option.dial_code === value.dial_code}
                                    sx={{
                                        minWidth: 185,
                                        '& .MuiInputBase-root': {
                                            height: '48px',
                                            borderRight: `1px solid ${borderColor2}`,
                                            borderRadius: 0,
                                            backgroundColor: 'transparent',
                                            px: 2,
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            InputProps={{ ...params.InputProps, disableUnderline: true, 
                                                style: {
                                                  color: errors.contactNumber ? 'red' : 'inherit',
                                             }
                                            }}
                                        />
                                    )}
                                />
                                <InputBase
                                    placeholder={`${t('membershipCards.mobileNumber')} *`}
                                    value={contactNumber}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        setContactNumber(digitsOnly);
                                        if (errors.contactNumber) {
                                            setErrors((prev) => ({ ...prev, contactNumber: '' }));
                                            setGeneralError('');
                                        }
                                    }}
                                      inputProps={{
                                        style: {
                                            color: errors.contactNumber ? 'red' : 'inherit',
                                        }
                                     }}
                                    sx={{ px: 2, flex: 1, height: '100%',
                                   
                                }}
                                />
                            </Box>

                            
                        </Box>

                        {generalError && (
                            <Typography color="error" variant="body2" sx={{ textAlign: "center", marginTop: 1 }}>
                                {generalError}
                            </Typography>
                        )}
                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <button type="button" style={{ color: btnColor, borderColor: btnColor }} className='cancel-btn' onClick={handleClose}>{t('membershipCards.cancel')}</button>
                            <button type="button" style={{ background: btnColor }} className='save-btn' onClick={handleSave}>{t('membershipCards.save')}</button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Use Notification Componen t */}
            <SuccessNotification
                open={successNotification}
                message={t(
                    selectedCard
                        ? 'serviceRequest.successMessage'
                        : 'serviceRequest.serviceRequestsSuccessMessage'
                )}
                severity="success"
                onClose={() => setSuccessNotification(false)}
            />
        </>
    );
}

export default ServiceRequestModal